import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import ModuleShell from "@/components/lunimar/LunimarModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import { Button } from "@/components/ui/button";
import CreditDashboard from "@/components/credit/CreditDashboard";
import CreditKeyTermsButton from "@/components/credit/CreditKeyTerms";
import {
  STARTING_PROFILE, SPENDING_CHOICES, SPENDING_AMOUNT, PAYMENT_CHOICES,
  AUTOPAY_CHOICES, RECOVERY_CHOICES, PAYOFF_CHOICES, STARTING_DEBTS, PAYOFF_EXTRA,
  initialState, applyDelta, utilizationPct, minPaymentFor, behaviorRating,
  type SimState, type DecisionImpact,
} from "@/data/creditSim";
import {
  ArrowRight, CreditCard, CheckCircle2, AlertTriangle, Activity,
  Target, FileText, Percent, Clock, TrendingDown, Banknote, Award,
} from "lucide-react";
import { useRef } from "react";
import {
  newAttemptId, startModuleAttempt, commitModuleAttempt, trackModule, trackAccountEffect,
} from "@/lib/learning/telemetry";
import { CREDIT_EVENTS, MODULE_CONCEPT_TAGS } from "@/lib/learning/moduleEvents";
import { useSfx } from "@/hooks/useSfx";
import { useSceneMusic } from "@/hooks/useSceneMusic";
import { useAccessTier } from "@/hooks/useAccessTier";
import { useInstructorVoice, useTriggerInstructorVoice, usePreloadInstructorVoice } from "@/hooks/useInstructorVoice";
import { useLaunchModuleAccess } from "@/components/launch/LaunchModuleGate";
import RetakeModuleDialog from "@/components/modules/RetakeModuleDialog";
import { useModuleRetake } from "@/hooks/useModuleRetake";
import type { MusicKey } from "@/lib/audio/musicMap";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

const CREDIT_VOICE_PRELOAD = [
  "credit.intro",
  "credit.prePaymentChoice",
  "credit.postDecision.paidFull",
  "credit.postDecision.minimumPaid",
  "credit.postDecision.missed",
  "credit.warning.utilizationHigh",
  "credit.consequence.scoreDrop",
  "credit.preQuizReview",
  "credit.completion",
] as const as unknown as string[];

/* ─── Steps ─── */
type Step =
  | "intro" | "profile" | "spend" | "util_quiz" | "autopay"
  | "payment" | "late_event" | "payoff" | "application" | "report"
  | "quiz" | "summary";

const STEPS: Step[] = [
  "intro","profile","spend","util_quiz","autopay",
  "payment","late_event","payoff","application","report","quiz","summary",
];

/* ─── Helpers ─── */

interface ChoiceProps {
  options: DecisionImpact[];
  value: string | null;
  onChange: (key: string) => void;
  disabledKey?: (key: string) => string | null;
}
function ChoiceList({ options, value, onChange, disabledKey }: ChoiceProps) {
  return (
    <div className="grid gap-3">
      {options.map((o) => {
        const reason = disabledKey?.(o.key);
        const disabled = !!reason;
        const active = value === o.key;
        return (
          <button
            key={o.key}
            disabled={disabled}
            onClick={() => !disabled && onChange(o.key)}
            className={`rounded-2xl border p-4 text-left transition-all ${
              active ? "border-primary bg-primary/10"
              : disabled ? "border-border/40 bg-card/30 opacity-60 cursor-not-allowed"
              : "border-border bg-card/60 hover:border-primary/40"
            }`}
          >
            <p className="font-heading text-sm font-bold text-foreground">{o.label}</p>
            {o.summary && <p className="mt-1 text-xs text-muted-foreground">{o.summary}</p>}
            <ul className="mt-2 space-y-1">
              {o.bullets.map((b, i) => (
                <li key={i} className="text-[11px] text-foreground/75">• {b}</li>
              ))}
            </ul>
            {reason && <p className="mt-2 text-[10px] text-destructive">{reason}</p>}
          </button>
        );
      })}
    </div>
  );
}

function Row({ label, value, bold, valueClass }:
  { label: string; value: string; bold?: boolean; valueClass?: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className={`text-muted-foreground ${bold ? "font-heading font-bold text-foreground" : ""}`}>{label}</span>
      <span className={`font-mono ${bold ? "font-heading font-bold text-foreground" : "text-foreground"} ${valueClass ?? ""}`}>{value}</span>
    </div>
  );
}

const Disclaimer = () => (
  <p className="text-[10px] text-muted-foreground/70 italic text-center">
    Educational simulation only. Simulated credit profile · no real bureaus involved.
  </p>
);

/* ─── Main component ─── */

const CreditManagementModule = () => {
  const navigate = useNavigate();
  const { tier: accessTier } = useAccessTier();
  const betaModuleUnlocked = useLaunchModuleAccess("credit_management");
  const isBetaModuleExperience = accessTier !== "admin" || betaModuleUnlocked;
  const backHref = isBetaModuleExperience ? "/dashboard" : "/curriculum";
  const backLabel = "Back to Dashboard";
  const { moduleGates, loading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();
  const { reverseAndReset, pending: retakePending } = useModuleRetake();
  const [retakeDialogOpen, setRetakeDialogOpen] = useState(false);
  const handleConfirmRetake = async () => {
    const r = await reverseAndReset("credit_management");
    if (r) { setRetakeDialogOpen(false); window.location.reload(); }
  };
  const { playSfx } = useSfx();
  const { playMoment: playVoiceMoment } = useInstructorVoice();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>("intro");
  const [sim, setSim] = useState<SimState>(() => initialState());
  const creditSceneKey: MusicKey = step === "intro"
    ? "moduleMissionStart"
    : step === "summary"
      ? "moduleCompletion"
      : step === "late_event" || step === "report"
        ? "reflectionState"
        : "creditSignal";
  useSceneMusic(creditSceneKey);
  // Voice: intro is requested from the learner's first click so browsers
  // with strict autoplay policies can start playback from a real gesture.
  // Beta v2 step-paint moments: fire-once-per-mount per momentId.
  useTriggerInstructorVoice("credit.intro", step === "intro");
  useTriggerInstructorVoice("credit.prePaymentChoice", step === "payment");
  useTriggerInstructorVoice(
    "credit.warning.utilizationHigh",
    step === "autopay" && utilizationPct(sim.balance, sim.creditLimit) >= 30,
  );
  // Fires once after the late-event animation, when the consequence
  // screen ("report") is on screen and the score actually dropped.
  useTriggerInstructorVoice(
    "credit.consequence.scoreDrop",
    step === "report" && sim.score < STARTING_PROFILE.score,
  );
  useTriggerInstructorVoice("credit.preQuizReview", step === "quiz");
  usePreloadInstructorVoice(CREDIT_VOICE_PRELOAD);

  // Delayed credit-profile panel reveal: distinct from module_start, fires
  // shortly after the profile step mounts so the holo cue lands on the panel.
  useEffect(() => {
    if (step !== "profile") return;
    const t = setTimeout(() => playSfx("holo_reveal"), 220);
    return () => clearTimeout(t);
  }, [step, playSfx]);

  const [spendChoice, setSpendChoice] = useState<string | null>(null);
  const [utilAnswer, setUtilAnswer] = useState<number | null>(null);
  const [autopayChoice, setAutopayChoice] = useState<string | null>(null);
  const [paymentChoice, setPaymentChoice] = useState<string | null>(null);
  const [recoveryChoice, setRecoveryChoice] = useState<string | null>(null);
  const [payoffChoice, setPayoffChoice] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const attemptRef = useRef(newAttemptId("credit_management"));
  const startedRef = useRef(false);
  const startTelemetry = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    void startModuleAttempt({
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: { starting_score: STARTING_PROFILE.score, credit_limit: STARTING_PROFILE.creditLimit },
      conceptTags: MODULE_CONCEPT_TAGS.credit_management,
    });
    void trackModule({
      eventType: CREDIT_EVENTS.STARTED,
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: { starting_score: STARTING_PROFILE.score, credit_limit: STARTING_PROFILE.creditLimit },
    });
  };

  const gate = useMemo(
    () => moduleGates.find((g) => g.moduleKey === "credit_management"),
    [moduleGates],
  );
  // Beta users are pre-validated by LaunchModuleGate; skip curriculum gating.
  const isLocked = isBetaModuleExperience ? false : !gate?.isUnlocked;
  const lockMsg = gate?.missingPrereqs?.length
    ? `Complete: ${gate.missingPrereqs.join(", ")}`
    : "Complete the prerequisite lessons to unlock.";

  /* ─── Stage labels ─── */
  const phaseLabel: Record<Step, string> = {
    intro: "Brief", profile: "Profile", spend: "Spending",
    util_quiz: "Utilization", autopay: "Autopay", payment: "Payment",
    late_event: "Late Event", payoff: "Payoff", application: "Application",
    report: "Report", quiz: "Knowledge Check", summary: "Complete",
  };

  const utilNow = utilizationPct(sim.balance, sim.creditLimit);
  const minPayNow = minPaymentFor(sim.balance);
  const correctUtilFromSpend = utilizationPct(SPENDING_AMOUNT[spendChoice ?? ""] ?? 0, STARTING_PROFILE.creditLimit);

  if (loading) return null;

  /* ─── Mechanics ─── */

  const onConfirmSpend = () => {
    if (!spendChoice) return;
    playSfx("decision_lock");
    startTelemetry();
    const amount = SPENDING_AMOUNT[spendChoice];
    const newBal = sim.balance + amount;
    const util = utilizationPct(newBal, sim.creditLimit);
    let delta = 0;
    let reason = "";
    if (spendChoice === "essentials") { delta = 2;  reason = "Low utilization purchase"; }
    if (spendChoice === "phone")      { delta = -5; reason = "Mid utilization (30%)"; }
    if (spendChoice === "shopping")   { delta = -25; reason = "High utilization (70%)"; }
    if (spendChoice === "max")        { delta = -55; reason = "Maxed out card (100%)"; }

    const next = applyDelta(
      { ...sim, balance: newBal, lateRisk: Math.min(100, sim.lateRisk + (util >= 70 ? 10 : 0)) },
      reason, delta, "Spending choice",
    );
    setSim(next);
    void trackModule({
      eventType: CREDIT_EVENTS.SPENDING,
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: { payment_choice: spendChoice, amount, utilization_percent: util, score_delta: delta },
    });
    void trackModule({
      eventType: CREDIT_EVENTS.UTILIZATION,
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: { utilization_percent: util, starting_balance: sim.balance, final_balance: newBal },
    });
    void trackAccountEffect({
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      eventType: "credit_score_changed",
      status: "draft",
      amount: delta,
      metadata: { reason },
    });
    setStep("util_quiz");
  };

  const onConfirmUtilQuiz = () => {
    if (utilAnswer === null) return;
    const isCorrect = utilAnswer === correctUtilFromSpend;
    playSfx(isCorrect ? "correct_soft" : "incorrect_soft");
    const next = applyDelta(
      sim,
      isCorrect ? "Understood utilization math" : "Misread utilization (no real penalty)",
      isCorrect ? 1 : 0,
      "Utilization check",
    );
    setSim(next);
    setStep("autopay");
  };

  const onConfirmAutopay = () => {
    if (!autopayChoice) return;
    playSfx("decision_lock");
    startTelemetry();
    let mode: SimState["autopay"] = "none";
    let delta = 0; let lateRiskDelta = 0; let reason = "";
    if (autopayChoice === "full")     { mode = "full";    delta = 6;  lateRiskDelta = -30; reason = "Autopay full balance"; }
    if (autopayChoice === "minimum")  { mode = "minimum"; delta = 3;  lateRiskDelta = -20; reason = "Autopay minimum"; }
    if (autopayChoice === "reminder") { mode = "none";    delta = 0;  lateRiskDelta = -5;  reason = "Manual reminder"; }
    if (autopayChoice === "none")     { mode = "none";    delta = -3; lateRiskDelta = 15;  reason = "No reminder"; }
    const next = applyDelta(
      { ...sim, autopay: mode, lateRisk: Math.max(0, Math.min(100, sim.lateRisk + lateRiskDelta)) },
      reason, delta, "Autopay setup",
    );
    setSim(next);
    void trackModule({
      eventType: CREDIT_EVENTS.PAYMENT_STRATEGY,
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: { payment_choice: autopayChoice, score_delta: delta },
    });
    setStep("payment");
  };

  const onConfirmPayment = () => {
    if (!paymentChoice) return;
    if (paymentChoice === "skip") {
      // Skipping payment is a meaningful negative financial consequence
      playSfx("money_loss");
      // Voice: reinforce that missed payments rewrite the credit story.
      playVoiceMoment("credit.postDecision.missed");
    } else if (paymentChoice === "minimum") {
      // Risky but not immediately costly — warn the learner
      playSfx("warning_risk");
      // Voice: name the trap of carrying a balance forward.
      playVoiceMoment("credit.postDecision.minimumPaid");
    } else {
      playSfx("decision_lock");
      // Delayed wallet update — safe payment resolves balance/score cleanly.
      setTimeout(() => playSfx("wallet_update"), 350);
      if (paymentChoice === "full") {
        // Voice: reinforce that paying in full protects the credit story.
        playVoiceMoment("credit.postDecision.paidFull");
      }
    }
    startTelemetry();
    let pay = 0; let delta = 0; let interest = 0; let reason = "";
    const monthlyRate = sim.apr / 100 / 12;
    if (paymentChoice === "full") {
      pay = sim.balance; delta = 10; interest = 0; reason = "Paid statement in full";
    } else if (paymentChoice === "minimum") {
      pay = minPayNow; interest = Math.round(sim.balance * monthlyRate * 100) / 100;
      delta = -3; reason = "Paid minimum only";
    } else if (paymentChoice === "partial") {
      pay = Math.round(sim.balance / 2);
      interest = Math.round((sim.balance - pay) * monthlyRate * 100) / 100;
      delta = 3; reason = "Paid half the balance";
    } else if (paymentChoice === "skip") {
      pay = 0; interest = Math.round(sim.balance * monthlyRate * 100) / 100;
      delta = -45; reason = "Skipped payment";
    }
    const newBal = Math.max(0, sim.balance - pay + interest);
    const newCash = sim.checking - pay;
    const lateFee = paymentChoice === "skip" ? 35 : 0;
    const next = applyDelta(
      {
        ...sim,
        balance: newBal + lateFee,
        checking: newCash,
        interestAccrued: sim.interestAccrued + interest,
        lateFees: sim.lateFees + lateFee,
        missedPayments: sim.missedPayments + (paymentChoice === "skip" ? 1 : 0),
        paymentsMade: sim.paymentsMade + (paymentChoice === "skip" ? 0 : 1),
        lateRisk: Math.max(0, Math.min(100, sim.lateRisk + (paymentChoice === "skip" ? 25 : -10))),
        interestSaved: sim.interestSaved + (paymentChoice === "full" ? Math.round(sim.balance * monthlyRate * 100) / 100 : 0),
      },
      reason, delta, "Payment decision",
    );
    setSim({ ...next, lateEventTriggered: paymentChoice === "skip" });
    void trackModule({
      eventType: CREDIT_EVENTS.PAYMENT_MADE,
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: { payment_choice: paymentChoice, amount: pay, interest_accrued: interest, score_delta: delta },
    });
    if (interest > 0) {
      void trackModule({
        eventType: CREDIT_EVENTS.INTEREST_ACCRUED,
        moduleSlug: "credit_management",
        attemptId: attemptRef.current,
        metadata: { interest_accrued: interest, apr: sim.apr },
      });
    }
    if (paymentChoice === "skip") {
      void trackModule({
        eventType: CREDIT_EVENTS.LATE_TRIGGERED,
        moduleSlug: "credit_management",
        attemptId: attemptRef.current,
        metadata: { late_fee: lateFee, missed_payment_count: sim.missedPayments + 1 },
      });
    }
    setStep("late_event");
  };

  const onConfirmRecovery = () => {
    if (!recoveryChoice) return;
    playSfx("decision_lock");
    let cashHit = 0; let delta = 0; let reason = "";
    if (recoveryChoice === "pay_autopay") {
      cashHit = Math.min(sim.checking, sim.balance);
      delta = 6;
      reason = "Recovered: paid down + autopay";
    } else if (recoveryChoice === "minimum") {
      cashHit = minPayNow;
      delta = 1; reason = "Recovered: minimum only";
    } else if (recoveryChoice === "courtesy") {
      cashHit = 0; delta = 2; reason = "Lender reversed one fee";
    } else if (recoveryChoice === "ignore") {
      cashHit = 0; delta = -20; reason = "Ignored — more damage";
    }
    const next = applyDelta(
      {
        ...sim,
        checking: sim.checking - cashHit,
        balance: Math.max(0, sim.balance - cashHit),
        lateFees: recoveryChoice === "courtesy" ? Math.max(0, sim.lateFees - 35) : sim.lateFees,
        autopay: recoveryChoice === "pay_autopay" ? "full" : sim.autopay,
        lateRisk: Math.max(0, Math.min(100,
          sim.lateRisk + (recoveryChoice === "pay_autopay" ? -25 :
                          recoveryChoice === "ignore" ? 20 : 0))),
      },
      reason, delta, "Late-payment recovery",
    );
    setSim(next);
    void trackModule({
      eventType: CREDIT_EVENTS.LATE_RESOLVED,
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: { payment_choice: recoveryChoice, amount: cashHit, score_delta: delta },
    });
    setStep("payoff");
  };

  const onConfirmPayoff = () => {
    if (!payoffChoice) return;
    playSfx("decision_lock");
    let interestSaved = 0; let delta = 0; let reason = "";
    if (payoffChoice === "avalanche") { interestSaved = 145; delta = 8; reason = "Avalanche: highest APR first"; }
    if (payoffChoice === "snowball")  { interestSaved = 95;  delta = 5; reason = "Snowball: smallest balance first"; }
    if (payoffChoice === "split")     { interestSaved = 50;  delta = 2; reason = "Split evenly"; }
    if (payoffChoice === "cash")      { interestSaved = 0;   delta = -8; reason = "Kept cash, debt grew"; }

    const next = applyDelta(
      { ...sim, payoffStrategy: payoffChoice, interestSaved: sim.interestSaved + interestSaved },
      reason, delta, "Debt payoff strategy",
    );
    setSim(next);
    void trackModule({
      eventType: CREDIT_EVENTS.PAYOFF_STRATEGY,
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: { payoff_strategy: payoffChoice, score_delta: delta },
    });
    setStep("application");
  };

  /* ─── Quiz pass/complete ─── */

  const onQuizPass = () => {
    setCompleted(true);
    completeModule("credit_management");
    playSfx("module_complete");
    // Voice: completion line — credit is built across many small choices.
    playVoiceMoment("credit.completion");
    // Persist final simulated credit score to the canonical credit_accounts row
    // so the Finance Center reflects the scenario outcome (both gains and losses).
    const finalScore = Math.max(300, Math.min(850, Math.round(sim.score)));
    const previousScore = STARTING_PROFILE.score;
    void (async () => {
      try {
        const { error } = await supabase.rpc("commit_sim_credit_score", {
          p_score: finalScore,
          p_reason: "credit_simulator_committed",
          p_metadata: {
            attempt_id: attemptRef.current,
            previous_score: previousScore,
            score_delta: finalScore - previousScore,
            scenario_id: "credit_management",
          },
        });
        if (error) console.error("commit_sim_credit_score failed", error);
        // Refresh any caches the Finance Center may read from.
        queryClient.invalidateQueries({ queryKey: ["credit"] });
        queryClient.invalidateQueries({ queryKey: ["credit_accounts"] });
        queryClient.invalidateQueries({ queryKey: ["finance-center"] });
        queryClient.invalidateQueries({ queryKey: ["sim-wallet"] });
        queryClient.invalidateQueries({ queryKey: ["student-finance-profile"] });
      } catch (e) {
        console.error("commit_sim_credit_score threw", e);
      }
    })();
    void trackModule({
      eventType: "credit_scenario_completed",
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: {
        previous_credit_score: previousScore,
        final_credit_score: finalScore,
        score_delta: finalScore - previousScore,
        scenario_id: "credit_management",
      },
    });
    void trackModule({
      eventType: CREDIT_EVENTS.REPORT,
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: {
        starting_score: STARTING_PROFILE.score,
        final_score: sim.score,
        score_delta: sim.score - STARTING_PROFILE.score,
        final_balance: sim.balance,
        utilization_percent: utilizationPct(sim.balance, sim.creditLimit),
        interest_accrued: sim.interestAccrued,
        late_fee: sim.lateFees,
        missed_payment_count: sim.missedPayments,
      },
    });
    void commitModuleAttempt({
      moduleSlug: "credit_management",
      attemptId: attemptRef.current,
      metadata: {
        final_score: sim.score,
        score_delta: sim.score - STARTING_PROFILE.score,
      },
    });
    setStep("summary");
  };

  /* ─── Render helpers ─── */

  const HUD = (
    <div className="space-y-3 mb-4">
      <CreditDashboard s={sim} phase={phaseLabel[step]} />
      <div className="flex justify-end">
        <CreditKeyTermsButton />
      </div>
    </div>
  );

  /* ─── Application screen — gated by final score ─── */

  const applicationResult = (() => {
    if (sim.score >= 720) return {
      title: "Approved — Best Terms",
      tone: "good" as const,
      body: "Your simulated score qualifies for low deposits and the best rates. Doors open.",
    };
    if (sim.score >= 650) return {
      title: "Approved — Higher Deposit / Rate",
      tone: "warn" as const,
      body: "You qualify but pay more — higher security deposit or interest rate.",
    };
    return {
      title: "Denied or Very Expensive",
      tone: "bad" as const,
      body: "Most lenders decline or quote you painful terms. Credit damage closes doors.",
    };
  })();

  /* ─── Render ─── */

  return (
    <ModuleShell
      realmKey="credit_management"
      title="Credit Management"
      stepIndex={STEPS.indexOf(step)}
      totalSteps={STEPS.length}
      isLocked={isLocked}
      lockMessage={lockMsg}
      isCompleted={completed}
      completedMessage="You operated a simulated credit profile end-to-end and passed the knowledge check."
      stepKey={step}
    >
      {step === "intro" && (
        <>
          <ModuleStepCard
            icon={<CreditCard className="w-8 h-8 text-primary" />}
            title="Operate Your Credit Profile"
            body="You have a credit card, a limit, a due date, and real choices. Use credit without letting debt damage your future. This is a simulated HeirWise credit profile — not your real credit."
            highlights={[
              "Every choice moves your simulated score and cash",
              "Pass the 5-question check (4/5) to commit your outcome",
              "Fail the check and the module effects are voided",
            ]}
            action={
              <Button onClick={() => { playSfx("module_start"); setStep("profile"); }} className="w-full gap-2">
                Open My Credit Profile <ArrowRight className="w-4 h-4" />
              </Button>
            }
          />
        </>
      )}

      {step === "profile" && (
        <div className="space-y-5">
          {HUD}
          <div className="rounded-2xl border border-border bg-card/60 p-4 space-y-1.5 text-xs">
            <Row label="Starting score" value={`${STARTING_PROFILE.score}`} />
            <Row label="Credit limit" value={`$${STARTING_PROFILE.creditLimit.toLocaleString()}`} />
            <Row label="APR" value={`${STARTING_PROFILE.apr}%`} />
            <Row label="Checking balance" value={`$${sim.checking.toLocaleString()}`} />
            <Row label="Payment due in" value={`${STARTING_PROFILE.daysToDue} days`} />
          </div>
          <Button onClick={() => setStep("spend")} className="w-full gap-2">
            Make a Spending Decision <ArrowRight className="w-4 h-4" />
          </Button>
          <Disclaimer />
        </div>
      )}

      {step === "spend" && (
        <div className="space-y-5">
          {HUD}
          <ModuleStepCard
            icon={<Target className="w-8 h-8 text-primary" />}
            title="What goes on the card this week?"
            body="You have a $2,000 credit limit and your current checking balance. Choose your spend — balance and utilization update immediately."
            action={<div />}
          />
          <ChoiceList options={SPENDING_CHOICES} value={spendChoice} onChange={setSpendChoice} />
          <Button onClick={onConfirmSpend} disabled={!spendChoice} className="w-full gap-2">
            Charge It <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {step === "util_quiz" && (
        <div className="space-y-5">
          {HUD}
          <ModuleStepCard
            icon={<Percent className="w-8 h-8 text-accent" />}
            title="Read Your Utilization"
            body={`Your limit is $${sim.creditLimit.toLocaleString()} and your balance is $${sim.balance.toLocaleString()}. What is your utilization?`}
            action={<div />}
          />
          <div className="grid grid-cols-2 gap-2">
            {(() => {
              const correct = correctUtilFromSpend;
              const candidates = new Set<number>([correct]);
              // Build plausible distractors around the correct answer, clamped 1-99
              const offsets = [correct * 2, correct + 10, correct + 25, Math.max(1, correct - 5), 30, 60, 90, 10];
              for (const v of offsets) {
                const n = Math.min(99, Math.max(1, Math.round(v)));
                if (n !== correct) candidates.add(n);
                if (candidates.size >= 4) break;
              }
              const opts = Array.from(candidates).slice(0, 4).sort((a, b) => a - b);
              return opts.map((pct) => {
              const active = utilAnswer === pct;
              return (
                <button
                  key={pct}
                  onClick={() => setUtilAnswer(pct)}
                  className={`rounded-2xl border p-3 text-sm font-mono ${
                    active ? "border-primary bg-primary/10" : "border-border bg-card/60 hover:border-primary/40"
                  }`}
                >
                  {pct}%
                </button>
              );
              });
            })()}
          </div>
          <p className="text-[11px] text-muted-foreground italic">
            HeirWise uses a simulated credit profile for learning. Your choices affect your HeirWise credit health score, not your real-world credit report.
          </p>
          {utilAnswer !== null && (
            <div className={`rounded-2xl border p-3 text-xs ${
              utilAnswer === correctUtilFromSpend
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                : "border-destructive/40 bg-destructive/10 text-destructive"
            }`}>
              {utilAnswer === correctUtilFromSpend
                ? `Correct — ${correctUtilFromSpend}% utilization.`
                : `Not quite. Balance ÷ limit = ${correctUtilFromSpend}%. We'll keep going.`}
            </div>
          )}
          <Button onClick={onConfirmUtilQuiz} disabled={utilAnswer === null} className="w-full gap-2">
            Continue <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {step === "autopay" && (
        <div className="space-y-5">
          {HUD}
          <ModuleStepCard
            icon={<Clock className="w-8 h-8 text-primary" />}
            title="Your Payment is Due in 7 Days"
            body="Choose how you protect yourself from missing the due date."
            action={<div />}
          />
          <ChoiceList options={AUTOPAY_CHOICES} value={autopayChoice} onChange={setAutopayChoice} />
          <Button onClick={onConfirmAutopay} disabled={!autopayChoice} className="w-full gap-2">
            Lock In Strategy <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {step === "payment" && (
        <div className="space-y-5">
          {HUD}
          <ModuleStepCard
            icon={<Banknote className="w-8 h-8 text-accent" />}
            title="Statement Due — Make the Payment"
            body={`Statement balance: $${sim.balance.toLocaleString()}. Minimum due: $${minPayNow}. Your checking: $${sim.checking.toLocaleString()}.`}
            action={<div />}
          />
          <ChoiceList
            options={PAYMENT_CHOICES}
            value={paymentChoice}
            onChange={setPaymentChoice}
            disabledKey={(key) => {
              if (key === "full" && sim.checking < sim.balance) return "Not enough in checking to pay in full.";
              if (key === "partial" && sim.checking < Math.round(sim.balance / 2)) return "Not enough in checking for a half payment.";
              return null;
            }}
          />
          <Button onClick={onConfirmPayment} disabled={!paymentChoice} className="w-full gap-2">
            Submit Payment <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {step === "late_event" && (
        <div className="space-y-5">
          {HUD}
          {sim.lateEventTriggered ? (
            <>
              <ModuleStepCard
                icon={<AlertTriangle className="w-8 h-8 text-destructive" />}
                title="Your Payment Due Date Passed"
                body="You did not pay on time. A $35 late fee was added, your simulated score dropped, and your lender flagged the missed payment."
                action={<div />}
              />
              <ChoiceList options={RECOVERY_CHOICES} value={recoveryChoice} onChange={setRecoveryChoice} />
              <Button onClick={onConfirmRecovery} disabled={!recoveryChoice} className="w-full gap-2">
                Respond <ArrowRight className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <ModuleStepCard
                icon={<CheckCircle2 className="w-8 h-8 text-emerald-400" />}
                title="Payment Posted On Time"
                body="Your account is in good standing. Payment history is the biggest factor in your simulated score — keep it clean."
                action={<div />}
              />
              <Button onClick={() => setStep("payoff")} className="w-full gap-2">
                Plan Your Debt Payoff <ArrowRight className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      )}

      {step === "payoff" && (
        <div className="space-y-5">
          {HUD}
          <ModuleStepCard
            icon={<TrendingDown className="w-8 h-8 text-primary" />}
            title="Debt Payoff Strategy"
            body={`You have $${PAYOFF_EXTRA} extra this month. Three debts compete for it.`}
            action={<div />}
          />
          <div className="rounded-2xl border border-border bg-card/60 p-3 space-y-1.5 text-xs">
            {STARTING_DEBTS.map((d) => (
              <Row key={d.name} label={d.name} value={`$${d.balance.toLocaleString()} · ${d.apr}% APR`} />
            ))}
            <div className="border-t border-border mt-2 pt-2">
              <Row bold label="Extra to deploy" value={`$${PAYOFF_EXTRA}`} />
            </div>
          </div>
          <ChoiceList options={PAYOFF_CHOICES} value={payoffChoice} onChange={setPayoffChoice} />
          <Button onClick={onConfirmPayoff} disabled={!payoffChoice} className="w-full gap-2">
            Deploy the $250 <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {step === "application" && (
        <div className="space-y-5">
          {HUD}
          <ModuleStepCard
            icon={<FileText className="w-8 h-8 text-accent" />}
            title="Future Application"
            body="You apply for a phone plan, apartment lease, and starter auto loan. Your final simulated score decides the outcome."
            action={<div />}
          />
          <div className={`rounded-2xl border p-4 text-sm ${
            applicationResult.tone === "good" ? "border-emerald-500/40 bg-emerald-500/10"
            : applicationResult.tone === "warn" ? "border-amber-500/40 bg-amber-500/10"
            : "border-destructive/40 bg-destructive/10"
          }`}>
            <p className="font-heading font-bold mb-1 text-foreground">{applicationResult.title}</p>
            <p className="text-xs text-foreground/80">{applicationResult.body}</p>
          </div>
          <Button onClick={() => setStep("report")} className="w-full gap-2">
            See My Credit Report <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {step === "report" && (
        <div className="space-y-5">
          {HUD}
          <ModuleStepCard
            icon={<Activity className="w-8 h-8 text-primary" />}
            title="Your Simulated Credit Report"
            body="Final outcome before the knowledge check. Pass 4/5 to commit this outcome and earn module rewards."
            action={<div />}
          />
          <div className="rounded-2xl border border-border bg-card/60 p-4 space-y-1.5 text-xs">
            <Row label="Starting score" value={`${STARTING_PROFILE.score}`} />
            <Row bold label="Final score" value={`${sim.score}`} />
            <Row label="Score change" value={`${sim.score - STARTING_PROFILE.score >= 0 ? "+" : ""}${sim.score - STARTING_PROFILE.score}`}
              valueClass={sim.score - STARTING_PROFILE.score >= 0 ? "text-emerald-400" : "text-destructive"} />
            <Row label="Final balance" value={`$${sim.balance.toLocaleString()}`} />
            <Row label="Utilization" value={`${utilNow}%`} />
            <Row label="Interest paid" value={`$${sim.interestAccrued.toFixed(2)}`} />
            <Row label="Late fees" value={`$${sim.lateFees}`} />
            <Row label="Payments made" value={`${sim.paymentsMade}`} />
            <Row label="Missed payments" value={`${sim.missedPayments}`}
              valueClass={sim.missedPayments > 0 ? "text-destructive" : "text-emerald-400"} />
            {sim.bestDecision && <Row label="Best decision" value={`${sim.bestDecision} (+${sim.bestDelta})`} />}
            {sim.worstDecision && <Row label="Worst decision" value={`${sim.worstDecision} (${sim.worstDelta})`} />}
            <div className="border-t border-border mt-2 pt-2">
              <Row bold label="Behavior rating" value={behaviorRating(sim)} />
            </div>
          </div>
          <Button onClick={() => setStep("quiz")} className="w-full gap-2">
            Take Knowledge Check <ArrowRight className="w-4 h-4" />
          </Button>
          <Disclaimer />
        </div>
      )}

      {step === "quiz" && (
        <ModuleQuiz
          moduleKey="credit_management"
          sampleSize={5}
          shuffleOptions
          onPass={onQuizPass}
          backHref={backHref}
          backLabel={backLabel}
        />
      )}

      {step === "summary" && (
        <div className="space-y-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
            <Award className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground">Credit Module Complete</h2>
          <p className="text-sm text-muted-foreground">
            Your simulated profile finished at <span className="font-mono text-foreground">{sim.score}</span> ({behaviorRating(sim)}).
            Practice this behavior in real life — that is where credit actually compounds.
          </p>
          <div className="flex flex-col gap-3 pt-2">
            <Button onClick={() => navigate("/finances")} className="w-full gap-2">
              View Financial Command Center <ArrowRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={() => navigate(backHref)}>{backLabel}</Button>
            <Button
              variant="ghost"
              onClick={() => setRetakeDialogOpen(true)}
              className="w-full text-muted-foreground hover:text-destructive"
            >
              Retake Module
            </Button>
          </div>
          <Disclaimer />
        </div>
      )}
      <RetakeModuleDialog
        open={retakeDialogOpen}
        onOpenChange={setRetakeDialogOpen}
        moduleDisplayName="Credit Management"
        onConfirm={handleConfirmRetake}
        pending={retakePending}
      />
    </ModuleShell>
  );
};

export default CreditManagementModule;